import { useQueries } from '@tanstack/react-query';
import { useSites } from './useSites';
import { dataService, type Site, type Logger, type Probe, type Measurement } from '../services/data';
import { calculateProbeStatus, aggregateProbeStatuses, type ProbeStatus } from '../utils/probeStatus';

export interface ProbeWithStats {
  probe: Probe;
  measurements: Measurement[];
  currentValue?: number;
  min24h?: number;
  max24h?: number;
  status: ProbeStatus;
}

export interface LoggerWithProbes {
  logger: Logger;
  probes: ProbeWithStats[];
  status: ProbeStatus;
}

export interface SiteWithData {
  site: Site;
  loggers: LoggerWithProbes[];
  status: ProbeStatus;
}

function getDateRange24h(): { start: string; end: string } {
  const end = new Date();
  const start = new Date();
  start.setHours(start.getHours() - 24);
  return { start: start.toISOString(), end: end.toISOString() };
}

export function useDashboardData() {
  // Étape 1: Récupérer tous les sites
  const { data: sites = [], isLoading: sitesLoading, error: sitesError } = useSites();

  // Étape 2: Récupérer les loggers pour chaque site — probes incluses dans la réponse
  const loggersQueries = useQueries({
    queries: sites.map((site) => ({
      queryKey: ['loggers', site.id],
      queryFn: () => dataService.getLoggers(site.id),
      enabled: !!site.id,
      refetchInterval: 300000, // 5 min
    })),
  });

  const allLoggers = loggersQueries.flatMap((query) => query.data || []);
  const loggersLoading = loggersQueries.some((query) => query.isLoading);

  // Étape 3: Récupérer les measurements (24h) pour chaque probe (parallèle)
  // Probes viennent directement de logger.probes — pas de requête getProbes séparée
  const allProbes: Probe[] = allLoggers.flatMap((logger) => (logger.probes ?? []) as Probe[]);
  const { start, end } = getDateRange24h();
  const measurementsQueries = useQueries({
    queries: allProbes.map((probe) => ({
      queryKey: ['measurements', probe.id, '24h'],
      queryFn: () => dataService.getMeasurements(probe.id, { start, end }),
      enabled: !!probe.id,
      refetchInterval: 60000,
    })),
  });

  const measurementsLoading = measurementsQueries.some((query) => query.isLoading);

  // Construire la structure hiérarchique
  const sitesWithData: SiteWithData[] = sites.map((site, siteIndex) => {
    const siteLoggers = loggersQueries[siteIndex]?.data || [];

    const loggersWithProbes: LoggerWithProbes[] = siteLoggers.map((logger) => {
      const loggerProbes = (logger.probes ?? []) as Probe[];

      const probesWithStats: ProbeWithStats[] = loggerProbes.map((probe) => {
        const probeIndex = allProbes.findIndex((p) => p.id === probe.id);
        const measurements = measurementsQueries[probeIndex]?.data || [];

        // Stats 24h — from ADV measurements only (for min/max/amplitude display)
        let min24h: number | undefined;
        let max24h: number | undefined;
        if (measurements.length > 0) {
          const values = measurements.map((m) => Number(m.value_calibrated));
          min24h = Math.min(...values);
          max24h = Math.max(...values);
        }

        // Current value: prefer ADV measurements (most recent first), fall back to last_value from logger response
        const currentValue: number | undefined =
          measurements.length > 0
            ? Number(measurements[0].value_calibrated)
            : probe.last_value != null
            ? Number(probe.last_value)
            : undefined;

        // Recency: probe is "online" if it has ADV data OR last_measured_at is within 25h
        // (covers GATT-only loggers that don't emit ADV but sync regularly via hub)
        const hasRecentMeasurements =
          measurements.length > 0 ||
          (probe.last_measured_at != null &&
            Date.now() - new Date(probe.last_measured_at).getTime() < 25 * 60 * 60 * 1000);

        const status = calculateProbeStatus(currentValue, probe, hasRecentMeasurements);

        return { probe, measurements, currentValue, min24h, max24h, status };
      });

      const loggerStatus = aggregateProbeStatuses(probesWithStats.map((p) => p.status));
      return { logger, probes: probesWithStats, status: loggerStatus };
    });

    const siteStatus = aggregateProbeStatuses(loggersWithProbes.map((l) => l.status));
    return { site, loggers: loggersWithProbes, status: siteStatus };
  });

  const totalAlarms = sitesWithData.reduce((count, site) => {
    return count + site.loggers.reduce((loggerCount, logger) => {
      return loggerCount + logger.probes.filter(
        (probe) => probe.status === 'alarm' || probe.status === 'warning'
      ).length;
    }, 0);
  }, 0);

  const isLoading = sitesLoading || loggersLoading || measurementsLoading;
  const error = sitesError;

  return { sites: sitesWithData, totalAlarms, isLoading, error };
}
